<template>
  <div class="ov-page">
    <div class="ov-header">
      <div>
        <div class="ov-header__title">{{ $t('detailTicket.ticketDetail') }}</div>
        <p class="ov-header__subtitle">{{ $t('detailTicket.subtitle') }}</p>
      </div>
      <div class="ov-header__actions">
        <button
          type="button"
          class="ov-btn ov-btn--secondary"
          @click="$router.replace({ path: '/selectionnerReservationBillet' })"
        >
          <v-icon small>mdi-arrow-left</v-icon>
          {{ $t('detailTicket.back') }}
        </button>
        <button
          type="button"
          class="ov-btn ov-btn--primary"
          :disabled="!reservationItem.designation || overlay"
          @click="printBookingTicket(reservationItem.designation)"
        >
          <v-progress-circular v-if="overlay" indeterminate size="16" width="2"></v-progress-circular>
          <v-icon v-else small>mdi-printer</v-icon>
          {{ $t('reservationTicket.print') }}
        </button>
      </div>
    </div>

    <article class="ov-card tk">
      <!-- Bandeau du billet -->
      <div class="tk__strip">
        <div class="tk__strip-main">
          <span class="tk__strip-icon"><v-icon small>mdi-ticket</v-icon></span>
          <div class="tk__strip-titles">
            <div class="tk__strip-kicker">{{ $t('detailTicket.reservation') }}</div>
            <div class="tk__strip-ref">{{ reservationItem.designation || '—' }}</div>
          </div>
        </div>
        <span
          class="ov-badge tk__status"
          :class="reservationItem.statusActualDesignation ? 'ov-badge--success' : 'ov-badge--neutral'"
        >
          {{ reservationItem.statusActualDesignation || '—' }}
        </span>
      </div>

      <!-- Trajet -->
      <div class="tk__section">
        <div class="tk__route">
          <div class="tk__city">
            <div class="ov-route__label">Départ</div>
            <div class="tk__city-name">{{ voyageDepartVilleLabel }}</div>
          </div>
          <div class="tk__route-line">
            <span class="tk__route-bus"><v-icon small>mdi-bus</v-icon></span>
          </div>
          <div class="tk__city tk__city--end">
            <div class="ov-route__label">Arrivée</div>
            <div class="tk__city-name">{{ voyageArriveeVilleLabel }}</div>
          </div>
        </div>

        <div class="ov-row ov-row--3 tk__trip">
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.offer') }}</div>
            <div class="tk__value">{{ reservationItem.offreVoyageDesignation || '—' }}</div>
          </div>
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.program') }}</div>
            <div class="tk__value">{{ reservationItem.programmeDesignation || '—' }}</div>
          </div>
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.station') }}</div>
            <div class="tk__value">{{ reservationItem.gareDesignation || '—' }}</div>
          </div>
        </div>
      </div>

      <!-- Perforation -->
      <div class="tk__perf" aria-hidden="true"><span class="tk__perf-line"></span></div>

      <!-- Voyageur & réservation -->
      <div class="tk__section">
        <div class="tk__group-title">
          <v-icon small>mdi-account</v-icon>
          {{ $t('detailTicket.client') }}
        </div>
        <div class="ov-row ov-row--3">
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.nameAndFirstNames') }}</div>
            <div class="tk__value">{{ clientFullName || '—' }}</div>
          </div>
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.email') }}</div>
            <div class="tk__value">{{ reservationItem.clientDetails.email || '—' }}</div>
          </div>
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.phone') }}</div>
            <div class="tk__value">{{ reservationItem.clientDetails.telephone || '—' }}</div>
          </div>
        </div>

        <div
          class="tk__seats"
          v-if="reservationItem.siegeAttribues && reservationItem.siegeAttribues.length > 0"
        >
          <div class="tk__label">
            <v-icon small class="tk__label-icon">mdi-seat</v-icon>
            {{ $t('detailTicket.assignedSeats') }}
          </div>
          <div class="tk__seat-list">
            <span
              v-for="(seat, index) in reservationItem.siegeAttribues"
              :key="index"
              class="ov-badge ov-badge--success ov-badge--plain"
            >
              <v-icon small>mdi-seat-passenger</v-icon>
              {{ $t('detailTicket.seat') }} {{ seat }}
            </span>
          </div>
        </div>

        <div class="tk__group-title tk__group-title--spaced">
          <v-icon small>mdi-ticket-confirmation-outline</v-icon>
          {{ $t('detailTicket.reservation') }}
        </div>
        <div class="ov-row ov-row--3">
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.reference') }}</div>
            <div class="tk__value tk__value--mono">{{ reservationItem.designation || '—' }}</div>
          </div>
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.reservedSeats') }}</div>
            <div class="tk__value">{{ reservationItem.nombrePlace || '—' }}</div>
          </div>
          <div class="tk__item">
            <div class="tk__label">{{ $t('detailTicket.reservationDateTime') }}</div>
            <div class="tk__value">{{ reservationDateLabel }}</div>
          </div>
        </div>
      </div>

      <!-- Montant -->
      <div class="tk__total">
        <span class="tk__total-label">{{ $t('detailTicket.amount') }}</span>
        <span class="tk__total-value">{{ reservationItem.montantTotalReservation || '—' }} FCFA</span>
      </div>
    </article>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>
      {{ errorMsg }}
    </v-alert>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import { showErrorMessage } from "@/modules/messages/messageProcess";
import {
  API_GENERATE_TICKET,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import axios from "axios";
export default {
  name: "DetailTicketReservation.vue",
  data() {
    return {
      overlay: false,
      errorMsg: null,
      reservationItem: {
        id: null,
        clientDetails: {
          addresse: null,
          nom: null,
          prenoms: null,
          email: null,
          telephone: null,
        },
        createdAt: null,
        createdBy: null,
        dateReservation: null,
        designation: null,
        gareDesignation: null,
        isOtherPerson: false,
        montantTotalReservation: null,
        nombrePlace: null,
        offreVoyageDesignation: null,
        programmeDesignation: null,
        statusActualDesignation: null,
        siegeAttribues: [],
      },

      ticketGenerated: {
        data: {
          designation: null,
        },
      },
    };
  },

  computed: {
    clientFullName() {
      if (!this.reservationItem || !this.reservationItem.clientDetails) return "";
      const n = this.reservationItem.clientDetails.nom || "";
      const p = this.reservationItem.clientDetails.prenoms || "";
      return `${n} ${p}`.trim();
    },

    reservationDateLabel() {
      const raw = this.reservationItem.dateReservation;
      if (!raw) return "—";

      const formatDateTime = (d) => {
        if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "—";
        const datePart = d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
        const timePart = d.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        return `${datePart} ${timePart}`;
      };

      if (raw instanceof Date) {
        return formatDateTime(raw);
      }

      if (typeof raw === "number") {
        const d = new Date(raw);
        return formatDateTime(d);
      }

      if (typeof raw === "string") {
        const dateOnlyMatch = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
        if (dateOnlyMatch) {
          const dd = Number(dateOnlyMatch[1]);
          const mm = Number(dateOnlyMatch[2]);
          const yyyy = Number(dateOnlyMatch[3]);
          const d = new Date(yyyy, mm - 1, dd, 0, 0, 0);
          return formatDateTime(d);
        }

        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) {
          return formatDateTime(d);
        }
      }

      return raw;
    },

    voyageDepartVilleLabel() {
      // Vérifier d'abord les champs directs
      if (this.reservationItem.villeDepartDesignation) return this.reservationItem.villeDepartDesignation;
      if (this.reservationItem.villeDepart) return this.reservationItem.villeDepart;
      
      // Essayer d'extraire depuis offreVoyageDesignation
      if (this.reservationItem.offreVoyageDesignation) {
        const parts = this.reservationItem.offreVoyageDesignation.split(/\s*[-–→>]+\s*/);
        if (parts[0]?.trim()) return parts[0].trim();
      }
      
      // Fallback sur la gare
      return this.reservationItem.gareDesignation || "—";
    },

    voyageArriveeVilleLabel() {
      // Vérifier d'abord les champs directs
      if (this.reservationItem.villeDestinationDesignation) return this.reservationItem.villeDestinationDesignation;
      if (this.reservationItem.villeArriveeDesignation) return this.reservationItem.villeArriveeDesignation;
      if (this.reservationItem.villeDestination) return this.reservationItem.villeDestination;
      if (this.reservationItem.villeArrivee) return this.reservationItem.villeArrivee;
      
      // Essayer d'extraire depuis offreVoyageDesignation
      if (this.reservationItem.offreVoyageDesignation) {
        const parts = this.reservationItem.offreVoyageDesignation.split(/\s*[-–→>]+\s*/);
        if (parts[1]?.trim()) return parts[1].trim();
      }
      
      return "—";
    },

    voyageDepartDateTimeLabel() {
      const date = this.reservationItem.dateDepart || this.reservationItem.programmeDateDepart;
      const heure = this.reservationItem.heureDepart || this.reservationItem.programmeHeureDepart;
      if (date && heure) return `${date} ${heure}`;
      return date || heure || "—";
    },

    voyageArriveeDateTimeLabel() {
      const date = this.reservationItem.dateArrivee || this.reservationItem.programmeDateArrivee;
      const heure = this.reservationItem.heureArrivee || this.reservationItem.programmeHeureArrivee;
      if (date && heure) return `${date} ${heure}`;
      return date || heure || "—";
    },
  },
  

  methods: {
    async editingBooking() {
      const booking = this.$store && this.$store.state
        ? this.$store.state.reservationBillet
        : null;

      if (booking) {
        this.reservationItem = booking;
      } else {
        this.$router.replace({ path: "/selectionnerReservationBillet" });
      }
    },

    //Generer le billet de voyage
    async printBookingTicket(designationBillet) {
      if (!designationBillet) {
        this.$swal.fire(
          this.$t('detailTicket.noTicketReference'),
          this.$t('detailTicket.cannotPrint'),
          "error"
        );
        return;
      }
      this.overlay = true;
      this.ticketGenerated.data.designation = designationBillet;
      try {
        const response = await axios.post(
          API_GENERATE_TICKET,
          this.ticketGenerated,
          {
            headers: HEADERS(this.$store.state.userAuthentified.token),
            responseType: 'blob',
          }
        );
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `billet-${designationBillet}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 15000);
      } catch (e) {
        this.errorMsg = e;
        showErrorMessage();
      } finally {
        this.overlay = false;
      }
    },
  },

  mounted() {
    this.editingBooking();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

/* ===== BILLET (carte d'embarquement) ===== */
.tk {
  max-width: 920px;
  margin: 0 auto;
}

.tk__strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 22px;
  background: $ov-navy;
  color: #ffffff;
}

.tk__strip-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.tk__strip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);

  .v-icon {
    color: #ffffff !important;
  }
}

.tk__strip-titles {
  min-width: 0;
}

.tk__strip-kicker {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.tk__strip-ref {
  font-size: 18px;
  font-weight: 900;
  letter-spacing: 0.04em;
  word-break: break-all;
}

.tk__status {
  background: #ffffff;
}

.tk__section {
  padding: 22px;
}

/* Trajet */
.tk__route {
  display: flex;
  align-items: center;
  gap: 16px;
}

.tk__city {
  min-width: 0;
  max-width: 40%;

  &--end {
    text-align: right;
  }
}

.tk__city-name {
  font-size: 24px;
  font-weight: 900;
  line-height: 1.2;
  color: $ov-navy;
  word-break: break-word;
}

.tk__route-line {
  position: relative;
  flex: 1;
  min-width: 40px;
  height: 0;
  border-top: 2px dashed rgba(21, 158, 114, 0.45);
}

.tk__route-bus {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid rgba(21, 158, 114, 0.35);

  .v-icon {
    color: $ov-green !important;
  }
}

.tk__trip {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid $ov-line;
}

/* Perforation : ligne pointillée + deux encoches */
.tk__perf {
  position: relative;
  height: 24px;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: $ov-soft;
    border: 1px solid $ov-line;
  }

  &::before {
    left: -13px;
  }

  &::after {
    right: -13px;
  }
}

.tk__perf-line {
  position: absolute;
  top: 50%;
  left: 22px;
  right: 22px;
  border-top: 2px dashed rgba(15, 23, 42, 0.15);
}

/* Champs */
.tk__group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $ov-green;

  .v-icon {
    color: $ov-green !important;
  }

  &--spaced {
    margin-top: 24px;
  }
}

.tk__item {
  min-width: 0;
}

.tk__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $ov-subtle;
  margin-bottom: 4px;
}

.tk__label-icon {
  color: $ov-green !important;
}

.tk__value {
  font-size: 15px;
  font-weight: 700;
  color: $ov-navy;
  word-break: break-word;

  &--mono {
    letter-spacing: 0.04em;
  }
}

.tk__seats {
  margin-top: 18px;
  padding: 14px 16px;
  border: 1px solid rgba(21, 158, 114, 0.25);
  border-radius: 12px;
  background: rgba(21, 158, 114, 0.05);
}

.tk__seat-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;

  .v-icon {
    color: inherit !important;
  }
}

/* Montant */
.tk__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-top: 1px solid $ov-line;
  background: $ov-soft;
}

.tk__total-label {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $ov-muted;
}

.tk__total-value {
  font-size: 22px;
  font-weight: 900;
  color: $ov-green;
}

/* Notifications */
.alert-error {
  width: 350px;
  max-width: calc(100vw - 32px);
}

@media (max-width: 760px) {
  .tk__strip,
  .tk__section,
  .tk__total {
    padding-left: 16px;
    padding-right: 16px;
  }

  .tk__city-name {
    font-size: 18px;
  }

  .tk__total-value {
    font-size: 18px;
  }
}

/* ===== IMPRESSION ===== */
@media print {
  .ov-page {
    padding: 0;
    background: #ffffff;
  }

  .ov-header__actions,
  .myalert {
    display: none !important;
  }

  .tk {
    max-width: none;
    box-shadow: none;
    border-color: #cbd5e1;
    break-inside: avoid;
  }

  .tk__strip {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .tk__perf::before,
  .tk__perf::after {
    background: #ffffff;
  }
}
</style>
